import { useTranslation } from "react-i18next";
import { IElecContract } from "../../types/contracts";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import { Button, SmartTable } from "@gisce/oficina-virtual-components";
import { LoadingAnimation } from "@/components/LoadingAnimation";
import { Discount, ErrorOutline, Receipt, ZoomIn } from "@mui/icons-material";
import { useSearchParams } from "react-router";
import { IElecContractsState } from "../../slices/elecContracts";
import { ActionButton } from "./ActionButton";
import { GoToContract, GoToFilteredInvoices } from "../../hooks/useContracts";
import useLangs from "@/languages/hooks/useLangs";
import { regional_settings } from "@/constants";
import ContractButtonGroup from "./ContractButtonGroup";

interface IContractsListTableProps {
  contracts: IElecContract[];
  status: IElecContractsState["status"];
  goToContract: GoToContract;
  goToFilteredInvoices: GoToFilteredInvoices;
}

export const ContractsElectricityListTable = ({
  contracts,
  status,
  goToContract,
  goToFilteredInvoices
}: IContractsListTableProps) => {
  const { t } = useTranslation();
  const { currentLang } = useLangs();
  const Settings = useSettingsContext();
  
  const [searchParams, _setSearchParams] = useSearchParams();

  const JSON_to_arrays = () => {
    let columns = [
      {
        title: t('common:text.contracts_contract'),
        key: "name",
      },
      {
        title: t('common:text.contracts_cups'),
        key: "cups",
      },
      {
        title: t('common:text.contracts_direction'),
        key: "address",
      },
      {
        title: t('common:text.contracts_product'),
        key: "tariff",
        removeSelf: !Settings?.contracts?.showProduct
      },
      {
        title: t('common:text.contracts_power'),
        key: "power",
        removeSelf: !Settings?.contracts?.showPower
      },
      {
        title: t('common:text.contracts_state'),
        key: "state",
      },
      {
        title: t('common:text.contracts_payment'),
        key: "payment",
      },
      {
        title: t('common:text.contracts_actions'),
        key: "actions",
      },
    ];

    // Remove any item with removeSelf set to true
    columns = columns.filter(item => !item.removeSelf);

    const contractsSource = !!contracts.length ? contracts
    .toSorted((a, b) => (a.state === "activa" ? -1 : 1))
    .map( (contract, index) => {

      const contractButtons = <ContractButtonGroup
        options={[
          {
            key: "contract",
            disabled: !contract.name,
            title: t("common:text.contract_view_detail"),
            onClick: () => goToContract(contract.id),
            startIcon: <ZoomIn />,
            children: t('common:text.contracts_view_contracts_button_button')
          },
          {
            key: "invoices",
            disabled: !contract.name,
            title: t("common:text.contract_related_invoices"),
            onClick: () => goToFilteredInvoices(contract),
            startIcon: <Receipt />,
            children: t('common:text.contracts_view_invoices_button_button')
          },
          ...(Settings.features.promotionalCode?.contractDetails?.applyCoupon ?
            [{
              key: "promocode",
              disabled: !contract.name,
              title: t("common:text.contract_view_detail"),
              onClick: () => goToContract(contract.id, 'applyCoupon'),
              startIcon: <Discount />,
              children: t('common:text.contracts_add_promocode')
            }] : []
          )
        ]}
      />

      const actionButton = <ActionButton 
          disabled={!contract.name} 
          onClick={
            () => goToContract(contract.id, searchParams.get("action"))
          } 
        />

      let contractStatus;
      switch (contract.state) {
        case 'esborrany':
          contractStatus = t('common:text.contract_status_draft');
          if (!contract.sign_date) {
            contractStatus = t('common:text.contract_status_signature_pending');
          }
          break;
        case 'activa':
        case 'modcontractual':
          contractStatus = t('common:text.contract_status_open');
          break;
        case 'baixa':
          contractStatus = t('common:text.contract_status_closed');
          break;
        default:
          contractStatus = t('common:text.contract_status_closed');
          break;
      }

      const hasDebt = contract.amount_debt > 0;
      const contractPayment = hasDebt ? `${t('common:text.contract_debt')}  (${contract.amount_debt.toLocaleString(currentLang)} €)` : t('common:text.contract_no_debt');;
      const contractedPowers = (contract.contracted_power_period && contract.contracted_power_period.length) ? contract.contracted_power_period.reduce((accumulator, currentValue) => {
        return `${accumulator} ${currentValue.period}: ${currentValue.power.toLocaleString(currentLang)} ${regional_settings.potence_unit}`;
      }, '') : '';

      const values = {
        id: index,
        name: contract.name,
        cups: contract.cups.name,
        address: `${contract.cups.street} - ${contract.cups.city}`,
        tariff: contract.pricelist.nom_comercial || contract.tariff.name,
        power: contractedPowers,
        state: contractStatus,
        payment: contractPayment,
        actions: searchParams.get("action") ? actionButton : contractButtons
      };

      return values;
    }) : [];

    return {
      columns,
      contractsSource
    };
  }

  const getStatusForItem = (item: any) => { //TODO: REMOVE ASAP
    if (item?.hasDebt) {
      return <ErrorOutline style={{color: '#FF0000'}}/>
    }
  }
  const adaptedData = JSON_to_arrays();
  
  return <SmartTable
    columns={ adaptedData?.columns ?? []} 
    dataSource={ adaptedData?.contractsSource ??[] }
    loading={status === 'loading'}
    onRowStatus={getStatusForItem}
    loadingComponent={<LoadingAnimation />}
    sortEnabled={Settings.features.smartTableSortEnabled}
  />
}